/**
 * ==== STANDALONE:FARMHAND_LITE:START ====
 * Feature: Tape weight calculator - USDA pig weight estimation from measurements
 * Tier: Lite ($3/mo)
 * Dependencies: React, api.ts (API_BASE_URL)
 * Generalization: Replace apiBase prop ("/bridge-club" | "/store-club") with
 *   generic /api/animals/ path. Core USDA formula logic is universal.
 * ============================================
 *
 * Tape Weight Calculator Component
 *
 * Allows farmers to estimate pig weight using the USDA tape measurement formulas:
 * - Standard Formula: (heart_girth^2 x body_length) / 400
 * - Simplified Formula: (10.17 x heart_girth) - 205.75
 *
 * Both formulas work best for pigs in the 100-300 lb range.
 */

import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../services/api';

interface TapeWeightResult {
  calculated_weight_lbs: number;
  formula_used: string;
  heart_girth_inches: number;
  body_length_inches: number | null;
}

interface TapeMeasurement {
  id: number;
  heart_girth_inches: number;
  body_length_inches: number | null;
  calculated_weight_lbs: number;
  formula_used: string;
  measured_at: string;
  notes: string;
}

interface TapeWeightCalculatorProps {
  animalId?: number;  // If provided, can save measurements
  onWeightCalculated?: (weight: number) => void;
  onMeasurementSaved?: () => void;
  apiBase?: string;  // "/bridge-club" or "/store-club"
  themeColor?: string;  // Primary accent color
}

const TapeWeightCalculator: React.FC<TapeWeightCalculatorProps> = ({
  animalId,
  onWeightCalculated,
  onMeasurementSaved,
  apiBase = '/bridge-club',
  themeColor = '#508590',
}) => {
  const styles = getStyles(themeColor);
  const [heartGirth, setHeartGirth] = useState<string>('');
  const [bodyLength, setBodyLength] = useState<string>('');
  const [formula, setFormula] = useState<'auto' | 'standard' | 'simplified'>('auto');
  const [result, setResult] = useState<TapeWeightResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [measurements, setMeasurements] = useState<TapeMeasurement[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Fetch measurement history when animalId changes
  useEffect(() => {
    if (animalId) {
      fetchMeasurements();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animalId]);

  const fetchMeasurements = async () => {
    if (!animalId) return;

    setLoadingHistory(true);
    try {
      const response = await fetch(`${API_BASE_URL}${apiBase}/animals/${animalId}/tape-measurements/`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setMeasurements(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Failed to fetch measurements', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const calculateWeight = async () => {
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}${apiBase}/tools/tape-weight-calculator/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          heart_girth_inches: parseFloat(heartGirth),
          body_length_inches: bodyLength ? parseFloat(bodyLength) : null,
          formula: formula,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.non_field_errors?.[0] || 'Calculation failed');
      }

      const data = await response.json();
      setResult(data);

      if (onWeightCalculated && data.calculated_weight_lbs) {
        onWeightCalculated(data.calculated_weight_lbs);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to calculate weight');
    } finally {
      setLoading(false);
    }
  };

  const saveMeasurement = async (updateAnimalWeight: boolean = false) => {
    if (!animalId || !result) return;

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}${apiBase}/animals/${animalId}/tape-measurements/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          heart_girth_inches: result.heart_girth_inches,
          body_length_inches: result.body_length_inches,
          formula_used: result.formula_used,
          notes: notes,
          update_animal_weight: updateAnimalWeight,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to save measurement');
      }

      if (onMeasurementSaved) {
        onMeasurementSaved();
      }

      // Refresh measurements list
      fetchMeasurements();

      // Reset form after successful save
      setHeartGirth('');
      setBodyLength('');
      setResult(null);
      setNotes('');
    } catch (err: any) {
      setError(err.message || 'Failed to save measurement');
    } finally {
      setSaving(false);
    }
  };

  const canCalculate = heartGirth && parseFloat(heartGirth) >= 10;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <span style={styles.icon}>📏</span>
        <h3 style={styles.title}>Pig Tape Weight Calculator</h3>
      </div>

      <p style={styles.description}>
        Estimate your pig's weight using a measuring tape. Wrap the tape around the heart girth (just behind the front legs) and optionally measure the body length (from ears to tail base).
      </p>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.form}>
        {/* Heart Girth - Required */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>Heart Girth (inches) *</label>
          <input
            type="text"
            inputMode="decimal"
            value={heartGirth}
            onChange={(e) => setHeartGirth(e.target.value)}
            onBlur={(e) => {
              const val = parseFloat(e.target.value);
              if (!isNaN(val) && val >= 10) {
                setHeartGirth(String(val));
              } else if (e.target.value && val < 10) {
                setHeartGirth('10');
              }
            }}
            style={styles.input}
            placeholder="45.5"
          />
          <span style={styles.helpText}>
            Measure around the chest, just behind the front legs
          </span>
        </div>

        {/* Body Length - Optional */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>Body Length (inches, optional)</label>
          <input
            type="text"
            inputMode="decimal"
            value={bodyLength}
            onChange={(e) => setBodyLength(e.target.value)}
            onBlur={(e) => {
              const val = parseFloat(e.target.value);
              if (!isNaN(val) && val >= 10) {
                setBodyLength(String(val));
              } else if (e.target.value && val < 10) {
                setBodyLength('10');
              } else {
                setBodyLength('');
              }
            }}
            style={styles.input}
            placeholder="38.0"
          />
          <span style={styles.helpText}>
            Measure from between the ears to the base of the tail
          </span>
        </div>

        {/* Formula Selection */}
        <div style={styles.inputGroup}>
          <label style={styles.label}>Formula</label>
          <div style={styles.formulaButtons}>
            <button
              style={{
                ...styles.formulaButton,
                backgroundColor: formula === 'auto' ? themeColor : '#fff',
                color: formula === 'auto' ? '#fff' : '#333',
              }}
              onClick={() => setFormula('auto')}
            >
              Auto
            </button>
            <button
              style={{
                ...styles.formulaButton,
                backgroundColor: formula === 'standard' ? themeColor : '#fff',
                color: formula === 'standard' ? '#fff' : '#333',
              }}
              onClick={() => setFormula('standard')}
            >
              Standard
            </button>
            <button
              style={{
                ...styles.formulaButton,
                backgroundColor: formula === 'simplified' ? themeColor : '#fff',
                color: formula === 'simplified' ? '#fff' : '#333',
              }}
              onClick={() => setFormula('simplified')}
            >
              Simplified
            </button>
          </div>
          <span style={styles.helpText}>
            {formula === 'standard' ? 'Requires body length. Formula: (girth² × length) / 400' :
             formula === 'simplified' ? 'Girth only. Formula: (10.17 × girth) - 205.75' :
             'Auto-selects based on available measurements'}
          </span>
        </div>

        {/* Calculate Button */}
        <button
          style={{
            ...styles.calculateButton,
            opacity: canCalculate && !loading ? 1 : 0.6,
          }}
          onClick={calculateWeight}
          disabled={!canCalculate || loading}
        >
          {loading ? 'Calculating...' : 'Calculate Weight'}
        </button>
      </div>

      {/* Result Display */}
      {result && (
        <div style={styles.resultBox}>
          <div style={styles.resultHeader}>Estimated Weight</div>
          <div style={styles.resultWeight}>
            {result.calculated_weight_lbs.toFixed(1)} lbs
          </div>
          <div style={styles.resultFormula}>
            Using {result.formula_used === 'standard' ? 'standard' : 'simplified'} formula
          </div>

          {/* Save Options (if animalId provided) */}
          {animalId && (
            <div style={styles.saveSection}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Notes (optional)</label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  style={styles.input}
                  placeholder="e.g., Before feeding"
                />
              </div>
              <div style={styles.saveButtons}>
                <button
                  style={styles.saveButton}
                  onClick={() => saveMeasurement(false)}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Measurement'}
                </button>
                <button
                  style={styles.useWeightButton}
                  onClick={() => saveMeasurement(true)}
                  disabled={saving}
                >
                  Save & Update Animal Weight
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Measurement History */}
      {animalId && measurements.length > 0 && (
        <div style={styles.historySection}>
          <button
            style={styles.historyToggle}
            onClick={() => setShowHistory(!showHistory)}
          >
            <span>Measurement History ({measurements.length})</span>
            <span>{showHistory ? '▲' : '▼'}</span>
          </button>

          {showHistory && (
            <div style={styles.historyList}>
              {loadingHistory ? (
                <div style={styles.historyLoading}>Loading...</div>
              ) : (
                measurements.map((m) => {
                  const date = new Date(m.measured_at);
                  const weight = parseFloat(String(m.calculated_weight_lbs)) || 0;
                  const girth = parseFloat(String(m.heart_girth_inches)) || 0;
                  const length = m.body_length_inches ? parseFloat(String(m.body_length_inches)) : null;
                  return (
                    <div key={m.id} style={styles.historyItem}>
                      <div style={styles.historyWeight}>
                        {weight.toFixed(1)} lbs
                      </div>
                      <div style={styles.historyDetails}>
                        <span style={styles.historyDate}>
                          {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span style={styles.historyMeasurements}>
                          Girth: {girth.toFixed(1)}"
                          {length && ` | Length: ${length.toFixed(1)}"`}
                        </span>
                        {m.notes && <span style={styles.historyNotes}>{m.notes}</span>}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      {/* Instructions */}
      <div style={styles.instructions}>
        <h4 style={styles.instructionsTitle}>How to Measure</h4>
        <ol style={styles.instructionsList}>
          <li><strong>Heart Girth:</strong> Wrap a soft measuring tape around the pig's body, just behind the front legs and over the shoulder blades. Keep the tape snug but not tight.</li>
          <li><strong>Body Length:</strong> Measure from between the ears, along the backbone, to the base of the tail. Keep the tape along the pig's topline.</li>
          <li><strong>Tips:</strong> Measure when the pig is standing naturally on a flat surface. For best results, measure at the same time each day (before feeding).</li>
        </ol>
      </div>
    </div>
  );
};

const getStyles = (themeColor: string): { [key: string]: React.CSSProperties } => ({
  container: {
    padding: '1.5rem',
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    maxWidth: '500px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  icon: {
    fontSize: '1.75rem',
  },
  title: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#333',
  },
  description: {
    fontSize: '0.875rem',
    color: '#666',
    margin: '0 0 1.5rem 0',
    lineHeight: 1.5,
  },
  error: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
    fontSize: '0.875rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
  },
  label: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#333',
  },
  input: {
    padding: '0.75rem 1rem',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    width: '100%',
    boxSizing: 'border-box',
  },
  helpText: {
    fontSize: '0.75rem',
    color: '#888',
  },
  formulaButtons: {
    display: 'flex',
    gap: '0.5rem',
  },
  formulaButton: {
    flex: 1,
    padding: '0.5rem',
    border: '2px solid #e0e0e0',
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  calculateButton: {
    backgroundColor: themeColor,
    color: '#fff',
    border: 'none',
    padding: '0.875rem',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: '0.5rem',
  },
  resultBox: {
    backgroundColor: '#e8f5e9',
    padding: '1.5rem',
    borderRadius: '12px',
    textAlign: 'center',
    marginTop: '1.5rem',
  },
  resultHeader: {
    fontSize: '0.75rem',
    color: '#388e3c',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '0.25rem',
  },
  resultWeight: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#2e7d32',
  },
  resultFormula: {
    fontSize: '0.875rem',
    color: '#388e3c',
    marginTop: '0.25rem',
  },
  saveSection: {
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '1px solid #a5d6a7',
  },
  saveButtons: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '0.75rem',
  },
  saveButton: {
    flex: 1,
    backgroundColor: '#fff',
    color: '#388e3c',
    border: '2px solid #388e3c',
    padding: '0.625rem',
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  useWeightButton: {
    flex: 1,
    backgroundColor: '#388e3c',
    color: '#fff',
    border: 'none',
    padding: '0.625rem',
    borderRadius: '6px',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  instructions: {
    marginTop: '1.5rem',
    paddingTop: '1rem',
    borderTop: '1px solid #e0e0e0',
  },
  instructionsTitle: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: '#333',
    margin: '0 0 0.75rem 0',
  },
  instructionsList: {
    margin: 0,
    padding: '0 0 0 1.25rem',
    fontSize: '0.8125rem',
    color: '#555',
    lineHeight: 1.6,
  },
  historySection: {
    marginTop: '1.5rem',
    borderTop: '1px solid #e0e0e0',
    paddingTop: '1rem',
  },
  historyToggle: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    backgroundColor: '#f5f5f5',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: '#333',
  },
  historyList: {
    marginTop: '0.75rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  historyLoading: {
    textAlign: 'center',
    padding: '1rem',
    color: '#666',
  },
  historyItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '1rem',
    padding: '0.75rem',
    backgroundColor: '#fafafa',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
  },
  historyWeight: {
    fontWeight: 600,
    fontSize: '1.125rem',
    color: '#333',
    minWidth: '80px',
  },
  historyDetails: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
  },
  historyDate: {
    fontSize: '0.8125rem',
    color: '#666',
  },
  historyMeasurements: {
    fontSize: '0.75rem',
    color: '#888',
  },
  historyNotes: {
    fontSize: '0.75rem',
    color: themeColor,
    fontStyle: 'italic',
  },
});

export default TapeWeightCalculator;
// ==== STANDALONE:FARMHAND_LITE:END ====
